import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { apiClient } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const GENERATE_ROLES = new Set(['team_lead', 'supervisor', 'manager', 'administrator', 'super_user', 'hr']);

const PasswordRecoveryRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const permissions = user?.permissions || [];
  // Match production: generate_code permission, with role fallback so stale localStorage
  // sessions still show Generate (same roles that can open this page).
  const canGenerate =
    permissions.includes('users.password_recovery.generate_code')
    || permissions.includes('users.password_recovery.assist')
    || (!!user?.role && GENERATE_ROLES.has(user.role));
  const [issuedCode, setIssuedCode] = useState<{ code: string; expiresInMinutes: number } | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['password-recovery-requests'],
    queryFn: async () => {
      const response = await apiClient.password.listSupervisorRequests();
      return response.data.data || [];
    },
    refetchInterval: 15000,
  });

  const handleGenerate = async (id: string) => {
    setGeneratingId(id);
    try {
      const response = await apiClient.password.generateSupervisorCode(id);
      const payload = response.data?.data;
      const code = payload?.code;
      if (!code) {
        toast.error('Code was not returned by the server. Try again.');
        return;
      }
      setIssuedCode({
        code,
        expiresInMinutes: payload?.expiresInMinutes ?? 10,
      });
      toast.success('Recovery code generated');
      queryClient.invalidateQueries({ queryKey: ['password-recovery-requests'] });
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Unable to generate recovery code');
    } finally {
      setGeneratingId(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Typography variant="h4" gutterBottom>Password Recovery Requests</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Generate a one-time recovery code for employees who requested assistance. Never ask for or set their permanent password.
      </Typography>
      <Card>
        <CardContent>
          {isLoading && <CircularProgress />}
          {isError && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={() => refetch()}>Retry</Button>}
            >
              Unable to load recovery requests.
            </Alert>
          )}
          {!isLoading && !isError && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Requested</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data || []).map((row: any) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Stack>
                        <Typography fontWeight={600}>{row.employeeName}</Typography>
                        <Typography variant="caption">{row.maskedEmail || row.employeeId}</Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>{row.requestedAt ? new Date(row.requestedAt).toLocaleString() : '—'}</TableCell>
                    <TableCell>{row.status}</TableCell>
                    <TableCell>{row.requestedFrom}</TableCell>
                    <TableCell align="right">
                      {canGenerate ? (
                        <Button
                          size="small"
                          variant="contained"
                          disabled={generatingId === row.id}
                          onClick={() => handleGenerate(row.id)}
                        >
                          {generatingId === row.id
                            ? 'Generating…'
                            : row.status === 'CODE_ISSUED'
                              ? 'Re-issue Code'
                              : 'Generate Recovery Code'}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No generate permission
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {(data || []).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography color="text.secondary">
                        No open password recovery requests. An employee must choose
                        &quot;Request Team Lead / Supervisor Assistance&quot; on Forgot Password
                        (web) or in the Windows Agent before a code can be generated here.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(issuedCode)} onClose={() => setIssuedCode(null)}>
        <DialogTitle>One-Time Recovery Code</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Communicate this code verbally. It will not be shown again. It expires in {issuedCode?.expiresInMinutes} minutes and does not become the employee password.
          </Alert>
          <Typography variant="h3" align="center" letterSpacing={6} fontWeight="bold">
            {issuedCode?.code}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuedCode(null)} variant="contained">Done</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default PasswordRecoveryRequestsPage;
